import {FC, useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {authOrgAPI, authUserAPI} from '@/core/api';
import {OrganizationService, UserService} from '@/utils/services';
import ForgotPasswordStep1 from './ForgorPasswordStep1';
import ForgotPasswordStep2 from './ForgotPasswordStep2';
import ForgotPasswordStep3 from './ForgotPasswordStep3';
import {useParams, useSearchParams} from 'react-router-dom';
import ForgotPasswordStep4 from './ForgotPasswordStep4';
import ForgotPasswordStep5 from './ForgotPasswordStep5';

interface ForgotPasswordModalProps {
	onCloseModal: () => void;
}

enum Steps {
	STEP_1,
	STEP_2,
	STEP_3,
	STEP_4,
	STEP_5,
}

const ForgotPasswordModal: FC<ForgotPasswordModalProps> = ({onCloseModal}) => {
	const [formStep, setFormStep] = useState(Steps.STEP_1);
	const [userEmail, setUserEmail] = useState('');

	const {slug} = useParams();
	const [searchParams] = useSearchParams();

	const handleReturnToStep1 = () => {
		setFormStep(Steps.STEP_1);
		setUserEmail('');
	};

	const handleSubmitStep1 = async (values: {email: string}) => {
		try {
			const apiInstance = slug ? authUserAPI : authOrgAPI;
			await OrganizationService.forgotPassword({toEmail: values.email, returnUrl: slug || ''}, apiInstance);
			setUserEmail(values.email);
			setFormStep(Steps.STEP_2);
		} catch (error) {
			setUserEmail(values.email);
			setFormStep(Steps.STEP_3);
			toast.error(getErrorMessage(error));
		}
	};

	const handleSubmitStep2 = async (values: {password: string; confirmPassword: string}) => {
		const code = searchParams.get('code');
		if (!code) {
			toast.error('Reset link is invalid or expired');
			return;
		}

		try {
			const apiInstance = slug ? authUserAPI : authOrgAPI;
			await UserService.resetPassword(
				{
					changingPassword: values.password,
					confirmChangePassword: values.confirmPassword,
					recoveryCode: code,
				},
				apiInstance
			);

			setFormStep(Steps.STEP_5);
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	useEffect(() => {
		const code = searchParams.get('code');

		if (code) {
			setFormStep(Steps.STEP_4);
		}
	}, [searchParams]);

	switch (formStep) {
		case Steps.STEP_1:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep1 onCloseModal={onCloseModal} handleSubmit={handleSubmitStep1} />
				</Box>
			);
		case Steps.STEP_2:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep2 onCloseModal={onCloseModal} handleSubmit={handleReturnToStep1} userEmail={userEmail} />
				</Box>
			);
		case Steps.STEP_3:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep3 onCloseModal={onCloseModal} handleSubmit={handleReturnToStep1} userEmail={userEmail} />
				</Box>
			);
		case Steps.STEP_4:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep4 onCloseModal={onCloseModal} handleSubmit={handleSubmitStep2} />
				</Box>
			);
		case Steps.STEP_5:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep5 onCloseModal={onCloseModal} />
				</Box>
			);
		default:
			return (
				<Box sx={{width: '100%'}}>
					<ForgotPasswordStep1 onCloseModal={onCloseModal} handleSubmit={handleSubmitStep1} />
				</Box>
			);
	}
};

export default ForgotPasswordModal;
